import { useCallback, useEffect, useState } from 'preact/hooks'
import type { AnalyticsViewDto } from '@/types/analytics'

interface AnalyticsViewState {
  data: AnalyticsViewDto | null
  loading: boolean
  error: string | null
  /** Re-runs the current fetcher (same scope/range) — e.g. for a manual refresh button. */
  refresh: () => void
}

/**
 * Minimal structural result the fetcher must resolve to. Kept deliberately loose so it
 * accepts both `ApiResponse` shapes in this repo (`@/api/client` and `@/types/company`),
 * which differ in optional fields.
 */
interface AnalyticsFetchResult {
  data?: AnalyticsViewDto
  error?: string
}

/**
 * Fetches an {@link AnalyticsViewDto} and tracks loading / error state, shared by the
 * Web / Campaign / Workspace analytics surfaces (ORB-328).
 *
 * Pass `fetcher = null` to represent "no valid scope yet" — loading is cleared and no
 * request is made. Otherwise pass a memoized fetcher (e.g. via `useMemo`); its identity
 * drives refetching, so it should change only when the scope/range/mock inputs change.
 *
 * An `active` flag prevents a superseded or unmounted request from writing state: it
 * guards both the result write and the loading reset, so a slow in-flight request can't
 * clobber a newer one's loading state. When the scope becomes invalid, the next run hits
 * the `!fetcher` guard and clears loading there.
 */
export function useAnalyticsView(
  fetcher: (() => Promise<AnalyticsFetchResult>) | null
): AnalyticsViewState {
  const [data, setData] = useState<AnalyticsViewDto | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Bumped by refresh() to re-run the effect with the same fetcher.
  const [reloadToken, setReloadToken] = useState(0)

  useEffect(() => {
    if (!fetcher) {
      setLoading(false)
      return
    }
    let active = true
    setLoading(true)
    setError(null)
    fetcher()
      .then(resp => {
        if (!active) return
        if (resp.error) setError(resp.error)
        else setData(resp.data ?? null)
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => { active = false }
  }, [fetcher, reloadToken])

  const refresh = useCallback(() => setReloadToken(token => token + 1), [])

  return { data, loading, error, refresh }
}
